import React, { useEffect, useState, useCallback } from "react";
import Navigation from "@/components/Navigation";
import PromotionBanner from "@/components/PromotionBanner";
import ImageViewer from "@/components/ImageViewer";
import { MapPin, Calendar, Phone, Facebook, Instagram } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

export default function Index() {
  const [count1, setCount1] = useState(0);
  const [count2, setCount2] = useState(0);
  const [count3, setCount3] = useState(0);
  const [count4, setCount4] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Optimized intersection observer setup
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-fade-in");
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -100px 0px" }
    );

    document.querySelectorAll(".animate-on-scroll").forEach((el) => {
      observer.observe(el);
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  // Separate useEffect for counters to improve performance
  useEffect(() => {
    if (!isVisible) return;
    
    const interval = setInterval(() => {
      setCount1((prev) => (prev < 1000 ? prev + 10 : 1000));
      setCount2((prev) => (prev < 350 ? prev + 5 : 350));
      setCount3((prev) => (prev < 50 ? prev + 1 : 50));
      setCount4((prev) => (prev < 3 ? prev + 0.1 : 3));
    }, 50);

    return () => clearInterval(interval);
  }, [isVisible]);

  const handleBooking = useCallback(() => {
    window.open("https://booksy.com/pl-pl/139856_chaba-thai-massage_masaz_20103_slupsk#ba_s=sh_1", "_blank");
  }, []);
  
  const handleFacebook = useCallback(() => {
    window.open("https://www.facebook.com/chabathai1/", "_blank");
  }, []);

  const handleInstagram = useCallback(() => {
    window.open("https://www.instagram.com/chabathaimassageslupsk/", "_blank");
  }, []);

  const carouselImages = [
    "/photos/1.webp",
    "/photos/2.webp", 
    "/photos/3.webp",
    "/photos/4.webp",
    "/photos/5.webp"
  ];

  const handleImageClick = (index: number) => {
    setCurrentImageIndex(index);
    setImageViewerOpen(true);
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % carouselImages.length);
  };

  const handlePreviousImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + carouselImages.length) % carouselImages.length);
  };

  const handleCloseImageViewer = () => {
    setImageViewerOpen(false);
  };

  return (
    <div className="bg-dark min-h-screen text-gold-light">
      <Navigation />
      
      {/* Hero Section */}
      <section className="h-screen relative flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/photos/tlo.jpg"
            alt="Thai Massage Background"
            width="1920"
            height="1080"
            className="w-full h-full object-cover object-center"
            loading="eager"
          />
          <div className="absolute inset-0 bg-dark/60 backdrop-blur-sm"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center">
          <h1 className="text-4xl md:text-7xl font-bold mb-6 animate-slide-down">
            ChaBa Thai Massage
          </h1>
          <p className="text-xl md:text-2xl mb-8 animate-slide-up max-w-2xl mx-auto">
            Odkryj prawdziwy relaks w sercu Słupska
          </p>
          <button 
            onClick={handleBooking}
            className="bg-gold text-dark px-8 py-3 rounded-full text-lg hover:bg-gold-dark transition-colors animate-fade-in"
          >
            Zarezerwuj wizytę
          </button>
        </div>
      </section>

      {/* Promotion Banner */}
      <PromotionBanner />

      {/* Statistics Section */}
      <section className="py-16 bg-dark">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div className="p-6 bg-dark-light rounded-lg animate-on-scroll opacity-0 transform translate-y-4">
              <h3 className="text-4xl font-bold text-gold mb-2">{count1}+</h3>
              <p>Zadowolonych klientów</p>
            </div>
            <div className="p-6 bg-dark-light rounded-lg animate-on-scroll opacity-0 transform translate-y-4">
              <h3 className="text-4xl font-bold text-gold mb-2">{count2}</h3>
              <p>Ocen 5/5 na Booksy</p>
            </div>
            <div className="p-6 bg-dark-light rounded-lg animate-on-scroll opacity-0 transform translate-y-4">
              <h3 className="text-4xl font-bold text-gold mb-2">{count3}+</h3>
              <p>Opinii 4.9/5 na Google</p>
            </div>
            <div className="p-6 bg-dark-light rounded-lg animate-on-scroll opacity-0 transform translate-y-4">
              <h3 className="text-4xl font-bold text-gold mb-2">{Math.round(count4 * 10) / 10}+</h3>
              <p>Lat doświadczenia</p>
            </div>
          </div>
        </div>
      </section>

      {/* Image Carousel Section */}
      <section className="py-16 bg-dark-light">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Carousel className="w-full" opts={{ align: "start", loop: true }}>
              <CarouselContent>
                {carouselImages.map((image, index) => (
                  <CarouselItem key={index} className="md:basis-1/2 lg:basis-1/3">
                    <div className="p-1">
                      <img
                        src={image}
                        alt={`Zdjęcie salonu ${index + 1}`}
                        className="w-full h-64 object-cover rounded-lg shadow-lg cursor-pointer hover:opacity-90 transition-opacity"
                        width="400"
                        height="256"
                        loading="lazy"
                        onClick={() => handleImageClick(index)}
                      />
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious />
              <CarouselNext />
            </Carousel>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-gradient-to-b from-dark to-dark-light">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold mb-12 text-center">Tajski masaż</h2>
            
            {/* Elastyczność */}
            <div className="flex flex-col md:flex-row items-center mb-16 animate-on-scroll opacity-0 transform translate-y-4">
              <img 
                src="/photos/zwiekszenie-elastycznosci.webp" 
                alt="Zwiększona elastyczność" 
                className="w-full md:w-1/2 rounded-lg mb-8 md:mb-0 md:mr-8" 
                width="600"
                height="400"
                loading="lazy"
              />
              <div>
                <h3 className="text-2xl font-bold mb-4">Zwiększenie elastyczności</h3>
                <p className="text-lg text-justify">
                  Tajski masaż to skuteczna metoda na poprawę elastyczności całego ciała. Poprzez delikatne rozciąganie i uciskanie określonych punktów, pomagamy rozluźnić napięte mięśnie i stawy. Regularne sesje masażu pozwalają zwiększyć zakres ruchu, poprawić postawę i zmniejszyć ryzyko kontuzji związanych z ograniczoną mobilnością.
                </p>
              </div>
            </div>

            {/* Redukcja stresu */}
            <div className="flex flex-col md:flex-row-reverse items-center mb-16 animate-on-scroll opacity-0 transform translate-y-4">
              <img 
                src="/photos/reduckja-stresu.webp" 
                alt="Redukcja stresu" 
                className="w-full md:w-1/2 rounded-lg mb-8 md:mb-0 md:ml-8" 
                width="600"
                height="400"
                loading="lazy"
              />
              <div>
                <h3 className="text-2xl font-bold mb-4">Redukcja stresu i napięcia</h3>
                <p className="text-lg text-justify">
                  W dzisiejszym zabieganym świecie, stres stał się nieodłącznym elementem naszego życia. Tajski masaż oferuje skuteczną metodę na jego redukcję. Głębokie techniki masażu i praca z punktami energetycznymi pomagają zrelaksować umysł i ciało, prowadząc do głębokiego uczucia spokoju i równowagi.
                </p>
              </div>
            </div>

            {/* Poprawa krążenia */}
            <div className="flex flex-col md:flex-row items-center animate-on-scroll opacity-0 transform translate-y-4">
              <img 
                src="/photos/poprawa-krazenia.webp" 
                alt="Poprawa krążenia" 
                className="w-full md:w-1/2 rounded-lg mb-8 md:mb-0 md:mr-8" 
                width="600"
                height="400"
                loading="lazy"
              />
              <div>
                <h3 className="text-2xl font-bold mb-4">Poprawa krążenia</h3>
                <p className="text-lg text-justify">
                  Prawidłowe krążenie krwi jest kluczowe dla zdrowia całego organizmu. Tajski masaż, poprzez specjalne techniki uciskowe i rozciągające, stymuluje przepływ krwi i limfy w organizmie. To przekłada się na lepsze dotlenienie tkanek, szybszą regenerację i wzmocnienie układu odpornościowego.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Learn More Button */}
      <section className="py-12 bg-dark text-center">
        <div className="container mx-auto px-4">
          <Link 
            to="/massage-types"
            className="inline-block bg-gold text-dark px-8 py-3 rounded-full text-lg hover:bg-gold-dark transition-colors"
          >
            Dowiedz się więcej o masażach
          </Link>
        </div>
      </section>

      {/* Quick Info Section */}
      <section className="py-16 bg-dark-light">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="flex items-center justify-center space-x-4 animate-on-scroll opacity-0 transform translate-y-4">
              <MapPin className="text-gold w-8 h-8 flex-shrink-0" />
              <div>
                <h3 className="font-semibold">Lokalizacja</h3>
                <p>Leszczynowa 20/U11</p>
                <p>76-200 Słupsk</p>
              </div>
            </div>
            <div className="flex items-center justify-center space-x-4 animate-on-scroll opacity-0 transform translate-y-4">
              <Calendar className="text-gold w-8 h-8 flex-shrink-0" />
              <div>
                <h3 className="font-semibold">Godziny otwarcia</h3>
                <p>Poniedziałek: 10:00 - 20:00</p>
                <p>Wtorek: 10:00 - 20:00</p>
                <p>Środa: Zamknięte</p>
                <p>Czwartek: 10:00 - 20:00</p>
                <p>Piątek: 10:00 - 20:00</p>
                <p>Sobota: 10:00 - 20:00</p>
                <p>Niedziela: 10:00 - 20:00</p>
              </div>
            </div>
            <div className="flex items-center justify-center space-x-4 animate-on-scroll opacity-0 transform translate-y-4">
              <Phone className="text-gold w-8 h-8 flex-shrink-0" />
              <div>
                <h3 className="font-semibold">Rezerwacje</h3>
                <p>733 503 287</p>
              </div>
            </div>
          </div>
        </div>
      </section>

{/* Footer */}
      <footer className="bg-dark py-8 border-t border-gold/10">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="mb-4 md:mb-0">
              <p className="text-gold-light/70">© 2025 ChaBa Thai Massage. Wszelkie prawa zastrzeżone.</p>
            </div>
            <div className="flex flex-col md:flex-row items-center space-x-0 md:space-x-4">
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleFacebook}
                  className="text-gold hover:text-gold-light transition-colors"
                  aria-label="Facebook"
                >
                  <Facebook size={24} />
                </button>
                <button
                  onClick={handleInstagram}
                  className="text-gold hover:text-gold-light transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram size={24} />
                </button>
              </div>
              {/* === POCZĄTEK: TUTAJ WKLEJ KOD === */}
              <p className="text-gold-light/70 text-sm mt-4 md:mt-0">
                Strona stworzona przez{" "}
                <a
                  href="https://prokod.pl"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-gold-light"
                >
                  ProKod.pl
                </a>
              </p>
              {/* === KONIEC: TUTAJ WKLEJ KOD === */}
            </div>
          </div>
        </div>
      </footer>

      {/* Fixed CTA Button */}
      <button 
        onClick={handleBooking}
        className="fixed bottom-8 right-8 bg-gold text-dark px-6 py-3 rounded-full shadow-lg hover:bg-gold-dark transition-colors z-50 flex items-center"
        aria-label="Zarezerwuj teraz"
      >
        <Calendar className="w-5 h-5 mr-2" />
        <span>Zarezerwuj teraz</span>
      </button>

      <ImageViewer
        images={carouselImages}
        currentIndex={currentImageIndex}
        isOpen={imageViewerOpen}
        onClose={handleCloseImageViewer}
        onNext={handleNextImage}
        onPrevious={handlePreviousImage}
      />
    </div>
  );
}
